import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import sharedStyles from '../AdminFilterShared/AdminFilterShared.module.css';
import styles from './AdminDateFilter.module.css';
import filterIcon from '@/assets/icons/filter.svg';

export type DateRangeBounds = {
  min?: string | null;
  max?: string | null;
};

export type DateRangeValue = {
  from?: string;
  to?: string;
};

type DatePreset = 'today' | 'tomorrow' | 'lastWeek' | 'next7Days' | 'thisMonth' | 'nextMonth';

type AdminDateFilterProps = {
  label: string;
  bounds?: DateRangeBounds;
  value: DateRangeValue;
  onChange: (next: DateRangeValue) => void;
};

const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/;

const toDateInputValue = (value?: string | null): string => {
  if (!value) return '';
  if (DATE_ONLY_RE.test(value)) {
    return value.slice(0, 10);
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return '';
  }

  return parsed.toISOString().slice(0, 10);
};

const toFromIso = (dateValue?: string): string | undefined => {
  if (!dateValue || !DATE_ONLY_RE.test(dateValue)) return undefined;
  return `${dateValue}T00:00:00.000Z`;
};

const toToIso = (dateValue?: string): string | undefined => {
  if (!dateValue || !DATE_ONLY_RE.test(dateValue)) return undefined;
  return `${dateValue}T23:59:59.999Z`;
};

const toValidDateOrNull = (dateValue: string): string | undefined | null => {
  if (!dateValue) {
    return undefined;
  }

  if (!DATE_ONLY_RE.test(dateValue)) {
    return null;
  }

  const parsed = new Date(`${dateValue}T00:00:00.000Z`);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }

  return parsed.toISOString().slice(0, 10) === dateValue ? dateValue : null;
};

const toPresetRange = (preset: DatePreset) => {
  const now = new Date();
  const today = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));

  const addDays = (date: Date, days: number) => {
    const next = new Date(date);
    next.setUTCDate(next.getUTCDate() + days);
    return next;
  };

  const toDateOnly = (date: Date) => date.toISOString().slice(0, 10);

  switch (preset) {
    case 'today': {
      const date = toDateOnly(today);
      return { from: date, to: date };
    }
    case 'tomorrow': {
      const date = toDateOnly(addDays(today, 1));
      return { from: date, to: date };
    }
    case 'lastWeek': {
      const from = toDateOnly(addDays(today, -6));
      const to = toDateOnly(today);
      return { from, to };
    }
    case 'next7Days': {
      const from = toDateOnly(today);
      const to = toDateOnly(addDays(today, 6));
      return { from, to };
    }
    case 'thisMonth': {
      const start = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), 1));
      const end = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth() + 1, 0));
      return { from: toDateOnly(start), to: toDateOnly(end) };
    }
    case 'nextMonth': {
      const start = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth() + 1, 1));
      const end = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth() + 2, 0));
      return { from: toDateOnly(start), to: toDateOnly(end) };
    }
    default:
      return { from: '', to: '' };
  }
};

export const AdminDateFilter = ({ label, value, onChange }: AdminDateFilterProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [popoverPosition, setPopoverPosition] = useState({ top: 0, left: 0, width: 300 });
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);

  const [localFrom, setLocalFrom] = useState(() => toDateInputValue(value.from));
  const [localTo, setLocalTo] = useState(() => toDateInputValue(value.to));

  useEffect(() => {
    setLocalFrom(toDateInputValue(value.from));
  }, [value.from]);

  useEffect(() => {
    setLocalTo(toDateInputValue(value.to));
  }, [value.to]);

  const isActive = Boolean(localFrom || localTo);

  const updatePopoverPosition = useCallback(() => {
    if (!triggerRef.current) return;

    const rect = triggerRef.current.getBoundingClientRect();
    const width = 300;
    const offsetTop = 8;
    const viewportPadding = 12;
    const left = Math.min(
      Math.max(rect.left - 300, viewportPadding),
      window.innerWidth - width - viewportPadding
    );

    setPopoverPosition({ top: rect.bottom + offsetTop, left, width });
  }, []);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (triggerRef.current?.contains(target) || popoverRef.current?.contains(target)) return;
      setIsOpen(false);
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    updatePopoverPosition();
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    window.addEventListener('resize', updatePopoverPosition);
    window.addEventListener('scroll', updatePopoverPosition, true);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
      window.removeEventListener('resize', updatePopoverPosition);
      window.removeEventListener('scroll', updatePopoverPosition, true);
    };
  }, [isOpen, updatePopoverPosition]);

  const applyFromTo = (nextFrom: string, nextTo: string) => {
    const validFrom = toValidDateOrNull(nextFrom);
    const validTo = toValidDateOrNull(nextTo);

    if (validFrom === null || validTo === null) {
      return;
    }

    const payload: DateRangeValue = {
      from: validFrom ? toFromIso(validFrom) : undefined,
      to: validTo ? toToIso(validTo) : undefined,
    };

    onChange(payload);
  };

  const onPreset = (preset: DatePreset) => {
    const next = toPresetRange(preset);
    setLocalFrom(next.from || '');
    setLocalTo(next.to || '');
    applyFromTo(next.from || '', next.to || '');
  };

  const reset = () => {
    setLocalFrom('');
    setLocalTo('');
    onChange({ from: undefined, to: undefined });
  };

  return (
    <div className={`${sharedStyles.root} ${isActive ? sharedStyles.rootActive : ''} ${isOpen ? sharedStyles.rootOpen : ''}`}>
      <span>{label}</span>
      <button
        type="button"
        className={sharedStyles.trigger}
        ref={triggerRef}
        onClick={() => setIsOpen((prev) => !prev)}
        aria-label={`Filter by ${label.toLowerCase()}`}
      >
        <img src={filterIcon} alt="Filter" className={sharedStyles.icon} />
      </button>

      {isOpen && createPortal(
        <div
          className={sharedStyles.popover}
          ref={popoverRef}
          style={{ top: `${popoverPosition.top}px`, left: `${popoverPosition.left}px`, width: `${popoverPosition.width}px` }}
        >
          <div className={styles.layout}>
            <div className={styles.presets}>
              <button type="button" className={styles.presetButton} onClick={() => onPreset('today')}>Today</button>
              <button type="button" className={styles.presetButton} onClick={() => onPreset('tomorrow')}>Tomorrow</button>
              <button type="button" className={styles.presetButton} onClick={() => onPreset('lastWeek')}>Last week</button>
              <button type="button" className={styles.presetButton} onClick={() => onPreset('next7Days')}>Next 7 days</button>
              <button type="button" className={styles.presetButton} onClick={() => onPreset('thisMonth')}>This month</button>
              <button type="button" className={styles.presetButton} onClick={() => onPreset('nextMonth')}>Next month</button>
            </div>

            <div className={styles.controls}>
              <label className={styles.inputCard}>
                <span className={styles.inputLabel}>From</span>
                <input
                  type="date"
                  className={styles.input}
                  value={localFrom}
                  onChange={(event) => {
                    const nextFrom = event.target.value;
                    setLocalFrom(nextFrom);
                    applyFromTo(nextFrom, localTo);
                  }}
                />
              </label>

              <label className={styles.inputCard}>
                <span className={styles.inputLabel}>To</span>
                <input
                  type="date"
                  className={styles.input}
                  value={localTo}
                  onChange={(event) => {
                    const nextTo = event.target.value;
                    setLocalTo(nextTo);
                    applyFromTo(localFrom, nextTo);
                  }}
                />
              </label>

              <button
                type="button"
                className={`${sharedStyles.reset} ${isActive ? sharedStyles.resetActive : ''}`}
                onClick={reset}
              >
                Reset All
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};
